/**
 * Creates a swipe detector for touch events.
 * @param {number} [threshold=10]  Minimum pixel movement to count as a swipe.
 * @returns {{ onTouchStart, onTouchEnd, getIsSwiped }}
 */
function createSwipeDetector(threshold = 10) {
	let startX = 0;
	let startY = 0;
	let isSwiped = false;

	/**
	 * Record the finger's starting position.
	 * @param {TouchEvent} event
	 */
	const onTouchStart = (event) => {
		const touch = event?.touches?.[0];
		if (!touch) return;
		startX = touch.clientX;
		startY = touch.clientY;
		isSwiped = false; // reset on every new touch
	};

	/**
	 * Compare end position to start position.
	 * Sets isSwiped = true only when movement exceeds the threshold.
	 * @param {TouchEvent} event
	 */
	const onTouchEnd = (event) => {
		const touch = event?.changedTouches?.[0];
		if (!touch) return;
		const deltaX = Math.abs(touch.clientX - startX);
		if (deltaX > threshold) {
			isSwiped = true;
			return;
		}
		const deltaY = Math.abs(touch.clientY - startY);
		isSwiped = deltaY > threshold;
	};

	/**
	 * Returns true if the last gesture was a swipe, false if it was a tap.
	 * @returns {boolean}
	 */
	const getIsSwiped = () => isSwiped;

	return { onTouchStart, onTouchEnd, getIsSwiped };
}

// Hoisted outside function to avoid recompilation on every call
const MATRIX_REGEX = /matrix\(\s*([^)]+)\s*\)/;

function getMatrixTranslateX(str) {
	const match = str.match(MATRIX_REGEX);
	if (match && match[1]) {
		const values = match[1].split(',').map((v) => v.trim());
		if (values.length === 6) {
			const result = parseFloat(values[4]);
			return Number.isNaN(result) ? null : result;
		}
	}
	return null;
}

export { createSwipeDetector, getMatrixTranslateX };
